import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";

/**
 * TourGuiado — overlay com "foco" (spotlight) em um elemento da tela e popup
 * explicativo. Usado para guiar o usuário pelos primeiros passos de
 * configuração de um template de fluxo recém-criado.
 *
 * Cada step pode ser:
 *  - Sem alvo (popup centralizado no meio da tela, overlay inteiro escuro)
 *    para introduções.
 *  - Com alvo via `selector` CSS (ex.: "[data-tour-target='save-btn']").
 *  - Com alvo via `nodeId` de nó do ReactFlow (ex.: "imob-menu-tipo").
 *
 * Posicionamento do popup: placement pode ser 'top' | 'bottom' | 'left' | 'right'.
 * Se não couber na direção escolhida, automaticamente cai no lado oposto.
 */

const PLACEMENT_OFFSET = 14; // px entre o spotlight e o popup
const HIGHLIGHT_PADDING = 6; // px de respiro ao redor do elemento destacado

const getTargetElement = (step) => {
	if (!step) return null;
	if (step.selector) {
		try {
			return document.querySelector(step.selector);
		} catch {
			return null;
		}
	}
	if (step.nodeId) {
		// ReactFlow renderiza cada nó com data-id="<id>".
		return document.querySelector(`[data-id="${step.nodeId}"]`);
	}
	return null;
};

const getEffectiveTarget = (el) => {
	if (!el) return null;
	let current = el;
	for (let i = 0; i < 3; i++) {
		const r = current.getBoundingClientRect();
		// Se estiver visível e com tamanho razoável, usa este.
		if (r.width >= 80 && r.height >= 28 && r.bottom > 0 && r.right > 0) return current;
		// Se for 0x0 (offscreen), não sobe — esperamos o scrollIntoView da próxima rodada.
		if (r.width <= 0 || r.height <= 0) return el;
		if (!current.parentElement || current.parentElement === document.body) break;
		current = current.parentElement;
	}
	return el;
};

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const TourGuiado = ({ steps, open, onSkip, onFinish, onStepChange, excludeRightPx = 0 }) => {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [rect, setRect] = useState(null); // { top, left, width, height } em viewport px
	const hasTargetRef = useRef(false);

	const currentStep = steps?.[currentIndex] || null;
	const totalSteps = steps?.length || 0;

	useEffect(() => {
		if (onStepChange) onStepChange(currentStep);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentStep, currentStep?.nodeId, currentStep?.selector]);

	// Calcula o rect do alvo (ou nulo, para tela inteira escura) SEMPRE que:
	// - o step mudar
	// - a janela redimensionar / dar scroll
	// - 200ms depois do ReactFlow reorganizar os nós (para posição estável)
	const recalc = useCallback(() => {
		const rawEl = getTargetElement(currentStep);
		if (!rawEl) {
			hasTargetRef.current = false;
			setRect(null);
			return;
		}
		try {
			rawEl.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
		} catch {
			/* noop */
		}
		// Aguarda painel lateral abrir + fitView do ReactFlow animar + 1 frame
		window.setTimeout(() => {
			const el = getEffectiveTarget(rawEl);
			const r = el.getBoundingClientRect();
			hasTargetRef.current = true;
			setRect({
				top: r.top - HIGHLIGHT_PADDING,
				left: r.left - HIGHLIGHT_PADDING,
				width: r.width + HIGHLIGHT_PADDING * 2,
				height: r.height + HIGHLIGHT_PADDING * 2,
			});
		}, 900);
	}, [currentStep]);

	useLayoutEffect(() => {
		if (!open) return undefined;
		recalc();
		// Dupla checagem para ReactFlow que às vezes renderiza em 2 etapas.
		const t1 = window.setTimeout(recalc, 120);
		const t2 = window.setTimeout(recalc, 380);
		const onResize = () => recalc();
		const onScroll = () => recalc();
		window.addEventListener("resize", onResize);
		window.addEventListener("scroll", onScroll, true);
		return () => {
			window.clearTimeout(t1);
			window.clearTimeout(t2);
			window.removeEventListener("resize", onResize);
			window.removeEventListener("scroll", onScroll, true);
		};
	}, [open, currentStep, recalc]);

	// Reinicia no passo 0 quando o tour é (re)aberto.
	useEffect(() => {
		if (open) setCurrentIndex(0);
	}, [open]);

	const goPrev = useCallback(() => {
		setCurrentIndex((i) => clamp(i - 1, 0, totalSteps - 1));
	}, [totalSteps]);

	const goNext = useCallback(() => {
		if (currentIndex >= totalSteps - 1) {
			onFinish?.();
		} else {
			setCurrentIndex((i) => i + 1);
		}
	}, [currentIndex, totalSteps, onFinish]);

	// Posição do popup relativo ao rect do spotlight.
	const popupStyle = useMemo(() => {
		const W = window.innerWidth;
		const H = window.innerHeight;
		const popupMaxW = 360;
		const popupMaxH = 300; // chute alto, o CSS vai crescer o que precisar

		if (!rect) {
			// Sem alvo: centraliza no meio da tela.
			return {
				top: `calc(50vh - ${popupMaxH / 2}px)`,
				left: `calc(50vw - ${popupMaxW / 2}px)`,
				width: popupMaxW,
			};
		}

		const placement = currentStep?.placement || "bottom";
		let top = 0;
		let left = 0;
		let finalPlacement = placement;

		const fitsBottom = rect.top + rect.height + PLACEMENT_OFFSET + popupMaxH <= H - 16;
		const fitsTop = rect.top - PLACEMENT_OFFSET - popupMaxH >= 16;
		const fitsRight = rect.left + rect.width + PLACEMENT_OFFSET + popupMaxW <= W - 16;
		const fitsLeft = rect.left - PLACEMENT_OFFSET - popupMaxW >= 16;

		if (placement === "bottom" && !fitsBottom && fitsTop) finalPlacement = "top";
		if (placement === "top" && !fitsTop && fitsBottom) finalPlacement = "bottom";
		if (placement === "right" && !fitsRight && fitsLeft) finalPlacement = "left";
		if (placement === "left" && !fitsLeft && fitsRight) finalPlacement = "right";

		switch (finalPlacement) {
			case "top":
				top = rect.top - PLACEMENT_OFFSET - popupMaxH;
				left = clamp(
					rect.left + rect.width / 2 - popupMaxW / 2,
					16,
					W - popupMaxW - 16
				);
				break;
			case "left":
				top = clamp(
					rect.top + rect.height / 2 - popupMaxH / 2,
					16,
					H - popupMaxH - 16
				);
				left = rect.left - PLACEMENT_OFFSET - popupMaxW;
				break;
			case "right":
				top = clamp(
					rect.top + rect.height / 2 - popupMaxH / 2,
					16,
					H - popupMaxH - 16
				);
				left = rect.left + rect.width + PLACEMENT_OFFSET;
				break;
			case "bottom":
			default:
				top = rect.top + rect.height + PLACEMENT_OFFSET;
				left = clamp(
					rect.left + rect.width / 2 - popupMaxW / 2,
					16,
					W - popupMaxW - 16
				);
				break;
		}

		return {
			top: `${top}px`,
			left: `${left}px`,
			width: `${popupMaxW}px`,
		};
	}, [rect, currentStep]);

	if (!open || !steps || steps.length === 0) return null;

	const isLast = currentIndex === totalSteps - 1;
	const isFirst = currentIndex === 0;

	return createPortal(
		<div
			className="pointer-events-none fixed inset-0 z-[9998]"
			role="dialog"
			aria-modal="true"
			aria-label="Tour de configuração do fluxo"
		>
			{/* Overlay com 4 "máscaras" formando um furo no meio (spotlight). */}
			{rect ? (
				<>
					{/* Topo */}
					<div
						className="absolute bg-black/60 backdrop-blur-[1px] pointer-events-auto"
						style={{ top: 0, left: 0, right: `${excludeRightPx}px`, height: `${rect.top}px` }}
					/>
					{/* Esquerda (abaixo do topo, acima do spotlight) */}
					<div
						className="absolute bg-black/60 backdrop-blur-[1px] pointer-events-auto"
						style={{
							top: `${rect.top}px`,
							left: 0,
							width: `${rect.left}px`,
							height: `${rect.height}px`,
						}}
					/>
					{/* Direita */}
					<div
						className="absolute bg-black/60 backdrop-blur-[1px] pointer-events-auto"
						style={{
							top: `${rect.top}px`,
							left: `${rect.left + rect.width}px`,
							right: `${excludeRightPx}px`,
							height: `${rect.height}px`,
						}}
					/>
					{/* Abaixo */}
					<div
						className="absolute bg-black/60 backdrop-blur-[1px] pointer-events-auto"
						style={{
							top: `${rect.top + rect.height}px`,
							left: 0,
							right: `${excludeRightPx}px`,
							bottom: 0,
						}}
					/>
					{/* Borda pulse em volta do spotlight (indica foco). */}
					<div
						className="absolute rounded-xl ring-4 ring-amber-400/70 pointer-events-none shadow-[0_0_0_99999px_rgba(0,0,0,0)] animate-pulse"
						style={{
							top: `${rect.top}px`,
							left: `${rect.left}px`,
							width: `${rect.width}px`,
							height: `${rect.height}px`,
						}}
					/>
				</>
			) : (
				<div className="absolute bg-black/70 backdrop-blur-sm pointer-events-auto" style={{ top:0, left:0, right: `${excludeRightPx}px`, bottom:0 }} />
			)}

			{/* Popup do passo atual */}
			<div
				className="pointer-events-auto absolute rounded-2xl border bg-background shadow-2xl p-4"
				style={popupStyle}
			>
				<div className="flex items-start justify-between gap-2">
					<div className="flex items-center gap-1.5">
						<span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-700">
							<Sparkles className="h-3.5 w-3.5" />
						</span>
						<h3 className="text-sm font-semibold leading-tight">
							{currentStep?.title || `Passo ${currentIndex + 1}`}
						</h3>
					</div>
					<button
						type="button"
						onClick={onSkip}
						className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
						aria-label="Fechar tour"
						title="Fechar tour"
					>
						<X className="h-4 w-4" />
					</button>
				</div>

				<p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
					{currentStep?.description}
				</p>

				{/* Barra de progresso + contador */}
				<div className="mt-3 flex items-center gap-2">
					<div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
						<div
							className="h-full bg-gradient-to-r from-amber-400 to-pink-500 transition-all"
							style={{
								width: `${((currentIndex + 1) / totalSteps) * 100}%`,
							}}
						/>
					</div>
					<span className="text-[11px] tabular-nums text-muted-foreground shrink-0">
						{currentIndex + 1}/{totalSteps}
					</span>
				</div>

				<div className="mt-3 flex items-center justify-between gap-2">
					<Button
						variant="ghost"
						size="sm"
						onClick={onSkip}
						className="text-muted-foreground hover:text-foreground px-2"
					>
						Pular
					</Button>
					<div className="flex items-center gap-1.5">
						<Button
							variant="outline"
							size="sm"
							onClick={goPrev}
							disabled={isFirst}
							className={cn(!isFirst && "opacity-100")}
						>
							<ChevronLeft className="h-4 w-4" />
							Anterior
						</Button>
						<Button size="sm" onClick={goNext}>
							{isLast ? "Concluir" : "Próximo"}
							{!isLast && <ChevronRight className="h-4 w-4" />}
						</Button>
					</div>
				</div>
			</div>
		</div>,
		document.body
	);
};

export default TourGuiado;
